import React from "react";
import { X } from "lucide-react";
import { getContrastColor } from "../util/getContrastColor";

/* =========================
   TYPES
========================= */

interface LandingCardProps {
  titulo?: string;
  descripcion?: string;
  imagenes?: string[];
  precio?: number;
  className?: string;
  variant?: "small" | "wide";
  styles?: any;
}

interface CardProps {
  titulo?: string;
  descripcion?: string;
  imagenes?: string[];
  precio?: number;
  styles?: any;
  onOpen?: () => void;
}

/* =========================
   SMALL CARD
========================= */

const SmallCard = ({
  titulo,
  descripcion,
  imagenes = [],
  precio,
  styles,
  onOpen,
}: CardProps) => {
  const imgs =
    imagenes.filter(Boolean).length > 0
      ? imagenes.filter(Boolean)
      : ["/img/logo/LogoLinkServs.png"];

  return (
    <div
      onClick={onOpen}
      className="
        group
        w-full
        h-[240px]

        flex
        flex-col

        rounded-2xl
        overflow-hidden
        shadow-md shadow-zinc-800
        bg-white/5
        backdrop-blur-xl

        border border-white/10

        cursor-pointer
        hover:z-[100]
        hover:rotate-x-1

        transition-all
        duration-300

        hover:scale-[1.06]
        hover:-translate-y-3
        hover:border-cyan-400/50
        hover:shadow-[0_35px_80px_rgba(0,255,255,.30)]
      "
    >
      {/* IMAGE */}

      <div className="relative basis-[40%] overflow-hidden bg-zinc-900">
        <img
          src={imgs[0]}
          draggable={false}
          className="
            w-full
            h-full
            object-cover

            transition-transform
            duration-700

            group-hover:scale-110
          "
        />

        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
      </div>

      {/* CONTENT */}

      <div className="basis-[45%] flex flex-col p-4 text-white min-h-0">
        <h3
          className="
           flex-none
           font-bold
           leading-tight
           truncate
           group-hover:text-cyan-300
           transition-colors
           duration-300
         "
          style={{
            color: styles?.h3Color,
            fontFamily: styles?.fontFamily,
            fontSize: "clamp(0.9rem, 0.75vw, 1.1rem)",
          }}
        >
          {titulo}
        </h3>

        <p
          className="
           mt-3
           flex-1
           min-h-0
           overflow-hidden
           leading-relaxed
       
           line-clamp-3
       
           group-hover:line-clamp-none
           group-hover:overflow-y-auto
       
           transition-all
           duration-300
         "
          style={{
            color: styles?.textColor,
            fontFamily: styles?.fontFamily,
            fontSize: "clamp(0.8rem, 0.7vw, 0.95rem)",
          }}
        >
          {descripcion}
        </p>
      </div>

      {/* FOOTER */}

      <div
        className="
          basis-[15%]

          px-4

          border-t
          border-white/10

          flex
          items-center
          justify-between

          bg-black/10
        "
      >
        <span className="text-[11px] text-white/50 uppercase tracking-wide">
          Precio
        </span>

        <span
          className="
           px-2
           py-1
           rounded-md
           border
           font-semibold
           whitespace-nowrap
           leading-none
           text-[clamp(0.7rem,0.8vw,0.9rem)]
         "
          style={{
            backgroundColor: `${styles?.accentColor}20`,
            borderColor: `${styles?.accentColor}40`,
            color: styles?.accentColor,
            fontFamily: styles?.fontFamily,
          }}
        >
          {precio ? `${precio} €` : "Consultar"}
        </span>
      </div>
    </div>
  );
};

/* =========================
   WIDE CARD
========================= */

const WideCard = ({
  titulo,
  descripcion,
  imagenes = [],
  precio,
  styles,
  onOpen,
}: CardProps) => {
  const imgs =
    imagenes.filter(Boolean).length > 0
      ? imagenes.filter(Boolean)
      : ["/img/logo/LogoLinkServs.png"];

  return (
    <div
      onClick={onOpen}
      className="
        group
        w-full
        h-[190px]

        flex
        flex-row

        rounded-2xl
        overflow-hidden
        shadow-md shadow-zinc-800
        bg-white/5
        backdrop-blur-xl

        border border-white/10

        cursor-pointer
        hover:z-[100]
        hover:rotate-x-1

        transition-all
        duration-300

        hover:scale-[1.06]
        hover:-translate-y-3
        hover:border-cyan-400/50
        hover:shadow-[0_35px_80px_rgba(0,255,255,.30)]
      "
    >
      {/* IMAGE */}

      <div className="relative w-[42%] h-full overflow-hidden bg-zinc-900">
        <img
          src={imgs[0]}
          draggable={false}
          className="
      w-full
      h-full
      object-cover
      transition-transform
      duration-700
      group-hover:scale-110
    "
        />

        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-transparent" />
      </div>

      {/* CONTENT */}

      <div className="w-[62%] flex flex-col p-5 text-white">
        <h3
          className="
           font-bold
           truncate
           leading-tight
     
           transition-colors
           duration-300
     
           group-hover:text-cyan-300
         "
          style={{
            color: styles?.h3Color,
            fontFamily: styles?.fontFamily,
          }}
        >
          {titulo}
        </h3>

        <p
          className="
      mt-3
      flex-1
      overflow-hidden
      leading-relaxed

      line-clamp-3

      group-hover:line-clamp-none
      group-hover:overflow-y-auto

      transition-all
      duration-300
    "
          style={{
            color: styles?.textColor,
            fontFamily: styles?.fontFamily,
          }}
        >
          {descripcion}
        </p>

        {/* PRECIO */}

        <div className="mt-4 flex justify-end items-center gap-2 border-t border-white/10 pt-3">
          <span className="text-sm text-white/50 font-medium">Precio</span>

          <span
            className="
        px-3
        py-1
        rounded-lg
        border
        font-semibold
        whitespace-nowrap
        text-[clamp(.75rem,.9vw,.95rem)]
      "
            style={{
              backgroundColor: `${styles?.accentColor}20`,
              borderColor: `${styles?.accentColor}40`,
              color: styles?.accentColor,
              fontFamily: styles?.fontFamily,
            }}
          >
            {precio ? `${precio} €` : "Consultar"}
          </span>
        </div>
      </div>
    </div>
  );
};

/* =========================
   LANDING CARD
========================= */

const LandingCard: React.FC<LandingCardProps & { onOpen?: () => void }> = ({
  variant = "small",
  titulo,
  descripcion,
  imagenes = [],
  precio,
  styles,
}) => {
  const [open, setOpen] = React.useState(false);
  const titleColor = getContrastColor(styles?.backgroundColor);
  const imgs =
    imagenes.filter(Boolean).length > 0
      ? imagenes.filter(Boolean)
      : ["/img/logo/LogoLinkServs.png"];

  return (
    <>
      {variant === "wide" ? (
        <WideCard
          titulo={titulo}
          descripcion={descripcion}
          imagenes={imagenes}
          precio={precio}
          styles={styles}
          onOpen={() => setOpen(true)}
        />
      ) : (
        <SmallCard
          titulo={titulo}
          descripcion={descripcion}
          imagenes={imagenes}
          precio={precio}
          styles={styles}
          onOpen={() => setOpen(true)}
        />
      )}

      {/* =========================
          MODAL
      ========================= */}

      {open && (
        <div
          className="
            fixed
            inset-0
            z-[9999]

            bg-black/90
            backdrop-blur-md

            flex
            items-center
            justify-center

            p-6

            animate-in
            fade-in
            duration-300
          "
          onClick={() => setOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="
              relative

              w-full
              max-w-6xl

              rounded-3xl
              overflow-hidden

              bg-zinc-900

              shadow-[0_30px_80px_rgba(0,0,0,.65)]

              animate-in
              zoom-in-95
              duration-300
            "
          >
            {/* BOTÓN CERRAR */}

            <button
              onClick={() => setOpen(false)}
              className="
                absolute

                top-5
                right-5

                z-20

                rounded-full

                bg-black/60

                p-3

                transition-all
                duration-300

                hover:scale-110
                hover:bg-red-500
              "
            >
              <X className="w-6 h-6 text-white" />
            </button>

            {/* IMAGEN */}

            <div className="relative bg-black flex items-center justify-center">
              <img
                src={imgs[0]}
                draggable={false}
                className="
                  max-h-[85vh]
                  w-auto

                  object-contain

                  transition-all
                  duration-500
                "
              />
            </div>

            {/* PIE */}

            <div
              className="
                absolute

                bottom-0
                left-0
                right-0

                bg-gradient-to-t
                from-black/95
                via-black/70
                to-transparent

                p-8
              "
            >
              <h2
                className="font-bold text-3xl mb-3"
                style={{
                  color: titleColor,
                  fontFamily: styles?.fontFamily,
                }}
              >
                {titulo}
              </h2>

              <p
                className="text-base leading-relaxed max-w-5xl"
                style={{
                  color: titleColor,
                  fontFamily: styles?.fontFamily,
                }}
              >
                {descripcion}
              </p>

              <div className="mt-6 flex justify-end">
                <span
                  className="
                  px-2
                  py-1
                  rounded-md
                  border
                  font-semibold
                  whitespace-nowrap
                  leading-none
                  text-[clamp(0.7rem,0.8vw,0.9rem)]
                "
                  style={{
                    backgroundColor: `${styles?.accentColor}20`,
                    borderColor: `${styles?.accentColor}40`,
                    color: styles?.accentColor,
                    fontFamily: styles?.fontFamily,
                  }}
                >
                  {precio ? `${precio} €` : "Consultar"}
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LandingCard;
